import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { AlertCircle, RefreshCcw, X, ArrowRight, CalendarClock, Check, Loader2, Plus } from 'lucide-react';
import { useAuth } from '../Auth';
import { apiService } from '../../services/api';
import {
  DEFAULT_PROJECT_PRIORITY_VALUE,
  PROJECT_PRIORITY_OPTIONS,
  getProjectPriorityInfo,
} from '../../utils/projectPriority';
import { LiveSecretaryPanel, PointSummaryCard, ProjectNewsCard } from './LiveSecretary';
import ReviewedProjects, { projectRecapState, reviewOrderOf } from './ReviewedProjects';
import FocusTechLive from './FocusTechLive';
import { ProjectSteering } from '../ProjectPage/roadmap/Steering';

const apiBaseUrl = apiService.getBaseUrl();
// Statuts d'un point de l'ordre du jour.
const POINT_STATUS = {
  open: { label: 'Ouvert', className: 'bg-slate-100 text-slate-600' },
  treated: { label: 'Traité', className: 'bg-emerald-50 text-emerald-700' },
  converted: { label: 'Transformé en tension', className: 'bg-violet-50 text-violet-700' },
};
const pointStatusInfo = (status) => {
  const norm = String(status || 'open').toLowerCase();
  if (norm === 'closed') return POINT_STATUS.treated;
  return POINT_STATUS[norm] || POINT_STATUS.open;
};
const IMPORT_PENDING_MS = 6000;
const focusMentionPattern = /@\[(circle|role|project|rule):(\d+)\|([^\]]+)\]/g;

const toStartOfDay = (value) => {
  const date = value instanceof Date ? new Date(value) : new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

const toEndOfDay = (value) => {
  const date = value instanceof Date ? new Date(value) : new Date(value);
  date.setHours(23, 59, 59, 999);
  return date;
};

const addDaysToDate = (value, days) => {
  const date = value instanceof Date ? new Date(value) : new Date(value);
  date.setDate(date.getDate() + days);
  return date;
};

const getIsoWeekStart = (value) => {
  const date = toStartOfDay(value);
  const diff = (date.getDay() + 6) % 7;
  date.setDate(date.getDate() - diff);
  return date;
};

const getIsoWeekEnd = (value) => toEndOfDay(addDaysToDate(getIsoWeekStart(value), 6));

const MeetingPanelTactical = ({
  activeMeeting,
  selectedCircle,
  meetingError,
  meetingParticipants,
  meetingPoints,
  preparedPoints = [],
  preparedPointsLoading = false,
  selectedMeetingPointId,
  selectedMeetingProjectId,
  meetingProjectReviews = [],
  meetingPointComposer,
  connectedUsers = [],
  realtimeConnected,
  presenceCount,
  employees = [],
  projects = [],
  circles = [],
  projectSprints = {},
  onFetchProjectSprints,
  onFetchProjectObjectives,
  meetingPhase,
  onSelectMeetingPhase,
  userIsSecretary,
  onCloseMeeting,
  onTransferSecretary,
  onInviteParticipant,
  onAddMeetingPoint,
  onImportMeetingPoint,
  onSelectMeetingPoint,
  onPointDecision,
  onPointNotesChange,
  onPointComposerUpdate,
  onCreatePointTask,
  onCreatePointObjective,
  onSelectMeetingProject,
  onFetchProjectReviews,
  onUpdateProjectReview,
  metricsRange,
  metricsRangeValid,
  meetingLive,
  liveActions,
  meetingFocus = null,
  meetingFocusList = [],
  focusActions,
  apiUrl,
}) => {
  const { getAuthHeaders, user } = useAuth();
  const currentUserId = user?.employee_id ?? user?.id;
  const [newPointTitle, setNewPointTitle] = useState('');
  const [inviteEmployeeId, setInviteEmployeeId] = useState('');
  const [importingPoints, setImportingPoints] = useState({});
  const [pointNotesDraft, setPointNotesDraft] = useState('');
  const [objectiveProjectSearch, setObjectiveProjectSearch] = useState('');
  const [projectObjectives, setProjectObjectives] = useState([]);
  const [objectivesLoading, setObjectivesLoading] = useState(false);
  const [objectivesError, setObjectivesError] = useState(null);
  const [projectFocusEntries, setProjectFocusEntries] = useState([]);
  const [projectFocusLoading, setProjectFocusLoading] = useState(false);
  const [projectFocusError, setProjectFocusError] = useState(null);
  const [projectHoursByEmployee, setProjectHoursByEmployee] = useState({});
  const [projectHoursLoading, setProjectHoursLoading] = useState(false);
  const [projectHoursError, setProjectHoursError] = useState(null);
  const [projectPriorityDraft, setProjectPriorityDraft] = useState('');
  const [projectPrioritySaveState, setProjectPrioritySaveState] = useState('idle');
  const isMountedRef = useRef(true);
  const pointNotesTypingRef = useRef(false);
  const pointNotesTypingTimer = useRef(null);
  const projectPriorityRequestRef = useRef(0);

  const meetingCircleId = activeMeeting?.circle_id || selectedCircle?.id;
  const childrenByParent = useMemo(() => {
    const map = {};
    (circles || []).forEach((circle) => {
      if (!circle?.parent_id) return;
      if (!map[circle.parent_id]) map[circle.parent_id] = [];
      map[circle.parent_id].push(circle);
    });
    return map;
  }, [circles]);

  const getDescendantIds = useCallback(
    (circleId) => {
      const ids = new Set();
      const stack = [circleId];
      while (stack.length > 0) {
        const current = stack.pop();
        if (!current || ids.has(current)) continue;
        ids.add(current);
        (childrenByParent[current] || []).forEach((child) => {
          if (child?.id) stack.push(child.id);
        });
      }
      return ids;
    },
    [childrenByParent]
  );

  const circleProjects = useMemo(() => {
    let list = projects || [];
    if (meetingCircleId) {
      const scopeIds = getDescendantIds(Number(meetingCircleId));
      list = (projects || []).filter((project) => {
        if (!project?.circle_id) return false;
        return scopeIds.has(Number(project.circle_id));
      });
    }
    return [...list].sort((a, b) => {
      const left = Number(a.project_number || a.id || 0);
      const right = Number(b.project_number || b.id || 0);
      return left - right;
    });
  }, [projects, meetingCircleId, getDescendantIds]);

  const meetingProjects = useMemo(() => {
    const inProgressOnly = circleProjects.filter((project) => {
      const status = String(project?.status || '').toLowerCase();
      return status === 'in_progress';
    });
    return inProgressOnly.sort((a, b) => {
      const left = Number(a.project_number || a.id || 0);
      const right = Number(b.project_number || b.id || 0);
      return left - right;
    });
  }, [circleProjects]);

  const selectedProject = useMemo(
    () => meetingProjects.find((project) => String(project.id) === String(selectedMeetingProjectId)) || null,
    [meetingProjects, selectedMeetingProjectId]
  );
  const projectIdsKey = useMemo(
    () => meetingProjects.map((project) => project.id).join(','),
    [meetingProjects]
  );
  const reviewByProjectId = useMemo(() => {
    const map = {};
    (meetingProjectReviews || []).forEach((entry) => {
      if (entry?.project_id === undefined || entry?.project_id === null) return;
      map[String(entry.project_id)] = entry;
    });
    return map;
  }, [meetingProjectReviews]);
  const currentProjectReview = selectedProject
    ? reviewByProjectId[String(selectedProject.id)]?.current || null
    : null;
  const getMeetingProjectPriorityInfo = useCallback((project) => {
    if (!project) return null;
    const currentPriority = reviewByProjectId[String(project.id)]?.current?.priority;
    return getProjectPriorityInfo(
      currentPriority !== null && currentPriority !== undefined ? currentPriority : project.pilotage_priority,
      DEFAULT_PROJECT_PRIORITY_VALUE
    );
  }, [reviewByProjectId]);
  const formatProjectSearchLabel = useCallback((project) => {
    if (!project) return '';
    const projectNumber = project.project_number || project.id;
    return `#${projectNumber} - ${project.name || `Projet #${project.id}`}`;
  }, []);

  const selectedPoint = meetingPoints.find((p) => String(p.id) === String(selectedMeetingPointId)) || null;
  // Projet proposé pour une tâche créée depuis un point : celui affiché en réunion, sinon l'unique projet en cours.
  const defaultTaskProjectId = useMemo(() => {
    if (selectedMeetingProjectId) return String(selectedMeetingProjectId);
    return meetingProjects.length === 1 ? String(meetingProjects[0].id) : '';
  }, [selectedMeetingProjectId, meetingProjects]);
  const defaultTaskForm = useMemo(() => ({ name: '', owner_id: '', project_id: '' }), []);
  const defaultObjectiveForm = useMemo(
    () => ({
      name: '',
      project_id: selectedMeetingProjectId ? String(selectedMeetingProjectId) : '',
      owner_id: '',
      effort: '1',
      description: '',
    }),
    [selectedMeetingProjectId]
  );
  const composerMode = meetingPointComposer?.mode === 'task' || meetingPointComposer?.mode === 'objective'
    ? meetingPointComposer.mode
    : null;
  const composerPointId = meetingPointComposer?.point_id !== undefined && meetingPointComposer?.point_id !== null
    ? String(meetingPointComposer.point_id)
    : null;
  const composerMatchesPoint = Boolean(selectedPoint?.id) && String(selectedPoint.id) === String(composerPointId || '');
  const showTaskModal = composerMatchesPoint && composerMode === 'task';
  const showObjectiveModal = composerMatchesPoint && composerMode === 'objective';
  const taskForm = useMemo(
    () => ({
      name: meetingPointComposer?.task_form?.name || '',
      owner_id: meetingPointComposer?.task_form?.owner_id !== undefined && meetingPointComposer?.task_form?.owner_id !== null
        ? String(meetingPointComposer.task_form.owner_id)
        : '',
      project_id: meetingPointComposer?.task_form?.project_id !== undefined && meetingPointComposer?.task_form?.project_id !== null
        ? String(meetingPointComposer.task_form.project_id)
        : '',
    }),
    [meetingPointComposer]
  );
  const taskProjectOptions = useMemo(() => {
    const options = meetingProjects.map((project) => ({ id: String(project.id), label: formatProjectSearchLabel(project) }));
    if (taskForm.project_id && !options.some((option) => option.id === taskForm.project_id)) {
      const current = (projects || []).find((project) => String(project.id) === taskForm.project_id);
      if (current) options.push({ id: String(current.id), label: formatProjectSearchLabel(current) });
    }
    return options;
  }, [meetingProjects, projects, taskForm.project_id, formatProjectSearchLabel]);
  const objectiveForm = useMemo(
    () => ({
      name: meetingPointComposer?.objective_form?.name || '',
      project_id: meetingPointComposer?.objective_form?.project_id !== undefined && meetingPointComposer?.objective_form?.project_id !== null
        ? String(meetingPointComposer.objective_form.project_id)
        : (selectedMeetingProjectId ? String(selectedMeetingProjectId) : ''),
      owner_id: meetingPointComposer?.objective_form?.owner_id !== undefined && meetingPointComposer?.objective_form?.owner_id !== null
        ? String(meetingPointComposer.objective_form.owner_id)
        : '',
      effort: meetingPointComposer?.objective_form?.effort !== undefined && meetingPointComposer?.objective_form?.effort !== null
        ? String(meetingPointComposer.objective_form.effort)
        : '1',
      description: meetingPointComposer?.objective_form?.description || '',
    }),
    [meetingPointComposer, selectedMeetingProjectId]
  );
  const objectiveProjectOptions = useMemo(
    () => (Array.isArray(projects) ? projects : []).map((project) => ({
      id: String(project.id),
      label: formatProjectSearchLabel(project),
    })),
    [projects, formatProjectSearchLabel]
  );
  const selectedObjectiveProjectLabel = useMemo(() => {
    if (!objectiveForm.project_id) return '';
    const selected = (projects || []).find((project) => String(project.id) === String(objectiveForm.project_id));
    return selected ? formatProjectSearchLabel(selected) : '';
  }, [projects, objectiveForm.project_id, formatProjectSearchLabel]);
  const objectiveProjectDatalistId = useMemo(
    () => `objective-project-picker-${activeMeeting?.id || 'default'}`,
    [activeMeeting?.id]
  );
  const visibleMeetingPoints = useMemo(
    () => meetingPoints.filter((point) => (point.status || '').toLowerCase() !== 'closed'),
    [meetingPoints]
  );
  const openPointsCount = visibleMeetingPoints.filter((point) => String(point.status || 'open').toLowerCase() === 'open').length;
  const selectedPointActions = Array.isArray(selectedPoint?.actions) ? selectedPoint.actions : [];

  const activeEmployees = useMemo(
    () => (employees || []).filter((emp) => emp?.is_active !== false),
    [employees]
  );
  const meetingParticipantIds = useMemo(
    () => new Set((meetingParticipants || []).map((participant) => String(participant.id))),
    [meetingParticipants]
  );
  const isParticipantConnected = useCallback(
    (participantId) => connectedUsers.some((u) => {
      const connectedId = u?.employee_id ?? u?.user_id ?? u?.id;
      return String(connectedId) === String(participantId);
    }),
    [connectedUsers]
  );
  const availableInvitees = useMemo(
    () => activeEmployees.filter((emp) => !meetingParticipantIds.has(String(emp.id))),
    [activeEmployees, meetingParticipantIds]
  );
  const employeeById = useMemo(() => {
    const map = {};
    (employees || []).forEach((emp) => {
      if (emp?.id === undefined || emp?.id === null) return;
      map[String(emp.id)] = emp;
    });
    return map;
  }, [employees]);
  const circleNameById = useMemo(() => {
    const map = {};
    (circles || []).forEach((circle) => {
      if (circle?.id === undefined || circle?.id === null) return;
      map[String(circle.id)] = circle.name || 'Rôle inconnu';
    });
    return map;
  }, [circles]);
  const getEmployeeName = useCallback(
    (id) => employeeById[String(id)]?.name || 'Membre inconnu',
    [employeeById]
  );
  const getProjectName = useCallback(
    (id) => (projects || []).find((proj) => String(proj.id) === String(id))?.name || 'Projet inconnu',
    [projects]
  );
  const secretary = meetingParticipants.find((p) => String(p.id) === String(activeMeeting?.created_by));
  // « Revue des indicateurs » retirée (20/10) : une réunion restée sur cette étape passe à la revue des projets.
  const effectiveMeetingPhase = meetingPhase === 'metrics' ? 'projects' : meetingPhase || 'inclusion';
  const meetingPhases = [
    { key: 'inclusion', label: "Tour d'inclusion", helper: 'Disponibilité des membres', badge: 'Inclusion' },
    { key: 'projects', label: 'Revue des projets', helper: 'Parcours des projets', badge: 'Projets' },
    { key: 'focus', label: 'Focus tech', helper: "Présentation d'un membre à l'équipe", badge: 'Focus tech' },
    { key: 'agenda', label: "Ordre du jour", helper: 'Points à traiter un par un', badge: 'Ordre du jour' },
    { key: 'closure', label: 'Clôture', helper: 'Fin de réunion', badge: 'Clôture' },
  ];
  const meetingPhaseInfo = meetingPhases.find((phase) => phase.key === effectiveMeetingPhase) || meetingPhases[0];
  const showInclusion = effectiveMeetingPhase === 'inclusion';
  const showProjects = effectiveMeetingPhase === 'projects';
  const showAgenda = effectiveMeetingPhase === 'agenda';
  const showFocus = effectiveMeetingPhase === 'focus';
  const showClosure = effectiveMeetingPhase === 'closure';
  const showProjectSidebar = showProjects;

  // Projets passés en revue (pour le rappel au-dessus du projet en cours) : ordre du direct s'il tourne, sinon
  // les projets choisis depuis l'ouverture de la page.
  const [visitedProjectIds, setVisitedProjectIds] = useState([]);
  useEffect(() => {
    setVisitedProjectIds([]);
  }, [activeMeeting?.id]);
  useEffect(() => {
    if (!selectedMeetingProjectId || !showProjects) return;
    const id = String(selectedMeetingProjectId);
    setVisitedProjectIds((prev) => (prev[prev.length - 1] === id ? prev : [...prev.filter((item) => item !== id), id]));
  }, [selectedMeetingProjectId, showProjects]);
  const reviewOrder = useMemo(
    () => reviewOrderOf(meetingLive?.project_order, visitedProjectIds),
    [meetingLive?.project_order, visitedProjectIds]
  );
  const reviewedIds = useMemo(() => new Set(reviewOrder), [reviewOrder]);
  const meetingProjectsById = useMemo(
    () => Object.fromEntries(meetingProjects.map((project) => [String(project.id), project])),
    [meetingProjects]
  );
  const selectedProjectIndex = selectedProject
    ? meetingProjects.findIndex((project) => String(project.id) === String(selectedProject.id))
    : -1;
  // Projet suivant dans la liste ; sans projet choisi, le premier.
  const nextProject = selectedProjectIndex >= 0
    ? meetingProjects[selectedProjectIndex + 1] || null
    : meetingProjects[0] || null;

  useEffect(() => {
    if (!selectedMeetingProjectId) return;
    if (!meetingProjects.length) return;
    const exists = meetingProjects.some((project) => String(project.id) === String(selectedMeetingProjectId));
    if (!exists && userIsSecretary && onSelectMeetingProject) {
      onSelectMeetingProject(null);
    }
  }, [selectedMeetingProjectId, meetingProjects, userIsSecretary, onSelectMeetingProject]);

  useEffect(() => {
    if (!projectIdsKey || !onFetchProjectReviews) return;
    onFetchProjectReviews(meetingProjects.map((project) => project.id));
  }, [projectIdsKey, meetingProjects, onFetchProjectReviews]);

  useEffect(() => {
    if (!selectedProject) {
      setProjectPriorityDraft('');
      setProjectPrioritySaveState('idle');
      return;
    }
    const savedPriority = currentProjectReview?.priority;
    const resolvedPriority = savedPriority !== null && savedPriority !== undefined
      ? savedPriority
      : selectedProject.pilotage_priority ?? DEFAULT_PROJECT_PRIORITY_VALUE;
    setProjectPriorityDraft(String(resolvedPriority));
    setProjectPrioritySaveState('idle');
  }, [selectedProject, currentProjectReview?.priority]);

  useEffect(() => {
    if (!showObjectiveModal) {
      setObjectiveProjectSearch('');
      return;
    }
    if (!selectedObjectiveProjectLabel) {
      setObjectiveProjectSearch('');
      return;
    }
    setObjectiveProjectSearch(selectedObjectiveProjectLabel);
  }, [showObjectiveModal, selectedObjectiveProjectLabel]);

  useEffect(() => {
    if (!selectedMeetingProjectId || !onFetchProjectSprints) return;
    onFetchProjectSprints(Number(selectedMeetingProjectId));
  }, [selectedMeetingProjectId, onFetchProjectSprints]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    if (!selectedMeetingProjectId || !onFetchProjectObjectives) {
      setProjectObjectives([]);
      setObjectivesError(null);
      setObjectivesLoading(false);
      return () => {
        mounted = false;
      };
    }
    setObjectivesLoading(true);
    setObjectivesError(null);
    onFetchProjectObjectives(selectedMeetingProjectId)
      .then((data) => {
        if (!mounted) return;
        setProjectObjectives(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (!mounted) return;
        setProjectObjectives([]);
        setObjectivesError(err?.message || 'Erreur lors du chargement des objectifs');
      })
      .finally(() => {
        if (!mounted) return;
        setObjectivesLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [selectedMeetingProjectId, onFetchProjectObjectives]);

  useEffect(() => {
    let cancelled = false;
    if (!selectedProject?.circle_id) {
      setProjectFocusEntries([]);
      setProjectFocusError(null);
      setProjectFocusLoading(false);
      return () => {
        cancelled = true;
      };
    }
    setProjectFocusLoading(true);
    setProjectFocusError(null);
    fetch(`${apiBaseUrl}/circle-focus?parent_id=${selectedProject.circle_id}`, {
      headers: getAuthHeaders(),
    })
      .then((response) => {
        if (!response.ok) throw new Error('Erreur lors du chargement des focus');
        return response.json();
      })
      .then((data) => {
        if (cancelled) return;
        setProjectFocusEntries(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setProjectFocusEntries([]);
        setProjectFocusError(err?.message || 'Erreur lors du chargement des focus');
      })
      .finally(() => {
        if (cancelled) return;
        setProjectFocusLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedProject?.circle_id, getAuthHeaders]);

  // Notes du point : rechargées au changement de point ; pendant la saisie, les échos du serveur sont ignorés.
  const selectedPointId = selectedPoint?.id ?? null;
  const selectedPointNotes = selectedPoint?.notes || '';
  const notesPointIdRef = useRef(null);
  useEffect(() => {
    const pointChanged = String(notesPointIdRef.current ?? '') !== String(selectedPointId ?? '');
    notesPointIdRef.current = selectedPointId;
    if (pointChanged) {
      pointNotesTypingRef.current = false;
      if (pointNotesTypingTimer.current) clearTimeout(pointNotesTypingTimer.current);
      setPointNotesDraft(selectedPointId ? selectedPointNotes : '');
      return;
    }
    if (pointNotesTypingRef.current) return;
    setPointNotesDraft(selectedPointNotes);
  }, [selectedPointId, selectedPointNotes]);

  const syncPointComposer = useCallback((nextComposer) => {
    if (!userIsSecretary || !selectedPoint || !onPointComposerUpdate) return;
    onPointComposerUpdate(nextComposer);
  }, [userIsSecretary, selectedPoint, onPointComposerUpdate]);

  const closePointComposer = useCallback(() => {
    syncPointComposer({
      mode: null,
      point_id: null,
      task_form: defaultTaskForm,
      objective_form: defaultObjectiveForm,
    });
  }, [syncPointComposer, defaultTaskForm, defaultObjectiveForm]);

  const openTaskComposer = useCallback(() => {
    if (!selectedPoint) return;
    syncPointComposer({
      mode: 'task',
      point_id: String(selectedPoint.id),
      task_form: { ...defaultTaskForm, project_id: defaultTaskProjectId },
      objective_form: defaultObjectiveForm,
    });
  }, [selectedPoint, syncPointComposer, defaultTaskForm, defaultTaskProjectId, defaultObjectiveForm]);

  const openObjectiveComposer = useCallback(() => {
    if (!selectedPoint) return;
    syncPointComposer({
      mode: 'objective',
      point_id: String(selectedPoint.id),
      task_form: defaultTaskForm,
      objective_form: defaultObjectiveForm,
    });
  }, [selectedPoint, syncPointComposer, defaultTaskForm, defaultObjectiveForm]);

  const updateTaskComposer = useCallback((updates) => {
    if (!selectedPoint) return;
    syncPointComposer({
      mode: 'task',
      point_id: String(selectedPoint.id),
      task_form: { ...taskForm, ...updates },
      objective_form: objectiveForm,
    });
  }, [selectedPoint, syncPointComposer, taskForm, objectiveForm]);

  const updateObjectiveComposer = useCallback((updates) => {
    if (!selectedPoint) return;
    syncPointComposer({
      mode: 'objective',
      point_id: String(selectedPoint.id),
      task_form: taskForm,
      objective_form: { ...objectiveForm, ...updates },
    });
  }, [selectedPoint, syncPointComposer, taskForm, objectiveForm]);

  useEffect(() => {
    return () => {
      if (pointNotesTypingTimer.current) clearTimeout(pointNotesTypingTimer.current);
    };
  }, []);


  const handleAddPoint = () => {
    const trimmed = (newPointTitle || '').trim();
    if (!trimmed || !onAddMeetingPoint) return;
    if (onAddMeetingPoint(trimmed) !== false) setNewPointTitle('');
  };

  // Points préparés avant la réunion : regroupés par auteur, présent ou absent, déjà à l'ordre du jour ou non.
  const agendaCirclePointIds = useMemo(() => new Set(
    (meetingPoints || [])
      .filter((point) => point.circle_point_id !== null && point.circle_point_id !== undefined)
      .map((point) => String(point.circle_point_id))
  ), [meetingPoints]);
  const preparedPointGroups = useMemo(() => {
    const groups = new Map();
    (preparedPoints || []).forEach((point) => {
      const authorKey = String(point.author_id ?? '');
      if (!groups.has(authorKey)) {
        const isMe = authorKey === String(currentUserId);
        groups.set(authorKey, {
          authorId: authorKey,
          name: isMe ? 'Mes points' : (point.author_name || employeeById[authorKey]?.name || 'Membre'),
          isMe,
          connected: isMe || isParticipantConnected(point.author_id),
          items: [],
        });
      }
      groups.get(authorKey).items.push({
        ...point,
        onAgenda: Boolean(point.on_agenda) || agendaCirclePointIds.has(String(point.id)),
      });
    });
    const list = Array.from(groups.values());
    list.forEach((group) => {
      group.items.sort((a, b) => Number(a.onAgenda) - Number(b.onAgenda)
        || String(a.created_at || '').localeCompare(String(b.created_at || '')));
    });
    return list.sort((a, b) => Number(b.isMe) - Number(a.isMe) || a.name.localeCompare(b.name, 'fr'));
  }, [preparedPoints, currentUserId, employeeById, isParticipantConnected, agendaCirclePointIds]);
  const preparedPendingCount = useMemo(
    () => preparedPointGroups.reduce((total, group) => total + group.items.filter((item) => !item.onAgenda).length, 0),
    [preparedPointGroups]
  );

  const handleImportPreparedPoint = (pointId) => {
    if (!onImportMeetingPoint) return;
    const key = String(pointId);
    if (onImportMeetingPoint(Number(pointId)) === false) return;
    setImportingPoints((prev) => ({ ...prev, [key]: true }));
    setTimeout(() => {
      if (!isMountedRef.current) return;
      setImportingPoints((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }, IMPORT_PENDING_MS);
  };

  const projectStatusLabel = (status) => {
    const norm = String(status || '').toLowerCase();
    if (norm === 'in_progress') return 'En cours';
    if (norm === 'done' || norm === 'completed') return 'Terminé';
    if (norm === 'blocked') return 'Bloqué';
    if (norm === 'not_started') return 'Non démarré';
    if (norm === 'proposition') return 'Proposition';
    return status || '';
  };

  const formatNumber = (value, decimals = 0) => {
    const num = Number(value || 0);
    if (Number.isNaN(num)) return '0';
    return num.toLocaleString('fr-FR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };
  const formatDateKey = useCallback((value) => {
    if (!value) return '';
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);
  const formatFocusText = useCallback((text) => (
    (text || '').replace(focusMentionPattern, (_match, _type, _id, label) => `@${label}`)
  ), []);
  const sumProjectHours = useCallback((hoursByDay, projectId) => {
    if (!hoursByDay || projectId === undefined || projectId === null) return 0;
    let total = 0;
    Object.values(hoursByDay).forEach((projects) => {
      if (!projects) return;
      const value = projects[String(projectId)];
      if (value) total += Number(value);
    });
    return total;
  }, []);
  const parseMetricDate = useCallback((value) => {
    if (!value) return null;
    if (value instanceof Date) return new Date(value);
    if (typeof value === 'string') {
      const trimmed = value.split('T')[0];
      const [year, month, day] = trimmed.split('-').map(Number);
      if (year && month && day) {
        return new Date(year, month - 1, day);
      }
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed;
  }, []);
  const weekRange = useMemo(() => {
    const today = new Date();
    return {
      start: getIsoWeekStart(today),
      end: getIsoWeekEnd(today),
    };
  }, []);
  const weekLabel = `${weekRange.start.toLocaleDateString('fr-FR')} - ${weekRange.end.toLocaleDateString('fr-FR')}`;
  const selectedRange = useMemo(() => {
    if (!metricsRange || !metricsRangeValid) return null;
    const start = parseMetricDate(metricsRange.start);
    const end = parseMetricDate(metricsRange.end);
    if (!start || !end) return null;
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    if (start > end) return null;
    return {
      start,
      end,
      label: `${start.toLocaleDateString('fr-FR')} - ${end.toLocaleDateString('fr-FR')}`,
    };
  }, [metricsRange, metricsRangeValid, parseMetricDate]);
  const activeRange = selectedRange || weekRange;
  const rangeLabel = selectedRange ? selectedRange.label : weekLabel;
  const rangeTitle = selectedRange ? 'Période sélectionnée' : 'Semaine en cours';
  const activeRangeKey = `${formatDateKey(activeRange.start)}:${formatDateKey(activeRange.end)}`;

  const projectFocusParticipants = useMemo(() => {
    if (!selectedProject?.id) return [];
    const token = `@[project:${selectedProject.id}|`;
    const grouped = new Map();
    (projectFocusEntries || []).forEach((entry) => {
      if (!entry?.focus_text || !entry.focus_text.includes(token)) return;
      if (entry.employee_id === undefined || entry.employee_id === null) return;
      const key = String(entry.employee_id);
      if (!grouped.has(key)) {
        const employee = employeeById[key];
        grouped.set(key, {
          employeeId: entry.employee_id,
          name: employee?.name || 'Membre inconnu',
          picture: employee?.picture || employee?.photo_url || employee?.google_picture || employee?.google_picture_url || null,
          roles: new Set(),
          focusTexts: new Set(),
        });
      }
      const target = grouped.get(key);
      const roleName = circleNameById[String(entry.circle_id)] || 'Rôle inconnu';
      if (roleName) target.roles.add(roleName);
      const cleanedFocus = formatFocusText(entry.focus_text || '');
      if (cleanedFocus) target.focusTexts.add(cleanedFocus);
    });
    const list = Array.from(grouped.values()).map((item) => ({
      ...item,
      roles: Array.from(item.roles),
      focusTexts: Array.from(item.focusTexts),
    }));
    list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [selectedProject?.id, projectFocusEntries, employeeById, circleNameById, formatFocusText]);

  const projectFocusParticipantIds = useMemo(
    () => projectFocusParticipants.map((participant) => participant.employeeId),
    [projectFocusParticipants]
  );
  const projectFocusParticipantKey = useMemo(
    () => projectFocusParticipantIds.map(String).sort().join('|'),
    [projectFocusParticipantIds]
  );

  useEffect(() => {
    let cancelled = false;
    // Les clés suffisent : période « début:fin » et participants « id|id ».
    const employeeIds = projectFocusParticipantKey ? projectFocusParticipantKey.split('|') : [];
    const [startDate, endDate] = (activeRangeKey || '').split(':');
    if (!selectedProject?.id || !employeeIds.length || !startDate || !endDate) {
      setProjectHoursByEmployee({});
      setProjectHoursLoading(false);
      setProjectHoursError(null);
      return () => {
        cancelled = true;
      };
    }
    setProjectHoursLoading(true);
    setProjectHoursError(null);
    Promise.all(
      employeeIds.map(async (employeeId) => {
        const response = await fetch(
          `${apiBaseUrl}/hours/employee/${employeeId}?start_date=${startDate}&end_date=${endDate}`,
          { headers: getAuthHeaders() }
        );
        if (!response.ok) {
          throw new Error('Erreur lors du chargement des heures');
        }
        const data = await response.json();
        return {
          employeeId,
          hours: sumProjectHours(data?.hours_by_day || {}, selectedProject.id),
        };
      })
    )
      .then((items) => {
        if (cancelled) return;
        const next = {};
        items.forEach((item) => {
          next[String(item.employeeId)] = item.hours;
        });
        setProjectHoursByEmployee(next);
      })
      .catch((err) => {
        if (cancelled) return;
        setProjectHoursByEmployee({});
        setProjectHoursError(err?.message || 'Erreur lors du chargement des heures');
      })
      .finally(() => {
        if (cancelled) return;
        setProjectHoursLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    selectedProject?.id,
    projectFocusParticipantKey,
    activeRangeKey,
    sumProjectHours,
    getAuthHeaders,
  ]);

  const activeSprintInfo = useMemo(() => {
    if (!selectedMeetingProjectId) return null;
    const sprints = projectSprints[String(selectedMeetingProjectId)] || [];
    if (!Array.isArray(sprints) || sprints.length === 0) return null;
    const ordered = [...sprints].sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const active = ordered.find((sprint) => {
      if (!sprint?.start_date || !sprint?.end_date) return false;
      const start = new Date(sprint.start_date);
      const end = new Date(sprint.end_date);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      return today >= start && today <= end;
    });
    if (active) return { sprint: active, label: 'Sprint en cours' };
    const upcoming = ordered.find((sprint) => {
      if (!sprint?.start_date) return false;
      const start = new Date(sprint.start_date);
      if (Number.isNaN(start.getTime())) return false;
      start.setHours(0, 0, 0, 0);
      return start > today;
    });
    if (upcoming) return { sprint: upcoming, label: 'Prochain sprint' };
    const last = [...ordered].sort((a, b) => new Date(b.end_date) - new Date(a.end_date))[0];
    return last ? { sprint: last, label: 'Dernier sprint' } : null;
  }, [selectedMeetingProjectId, projectSprints]);

  const activeSprint = activeSprintInfo?.sprint || null;
  const sprintDatesLabel = activeSprint?.start_date && activeSprint?.end_date
    ? `Du ${new Date(activeSprint.start_date).toLocaleDateString('fr-FR')} au ${new Date(activeSprint.end_date).toLocaleDateString('fr-FR')}`
    : null;

  const sprintObjectiveSummary = useMemo(() => {
    if (!activeSprint?.id) return { total: 0, done: 0, todo: 0, percent: 0 };
    const sprintObjectives = (projectObjectives || []).filter(
      (objective) => String(objective.sprint_id) === String(activeSprint.id)
    );
    const done = sprintObjectives.filter(
      (objective) => String(objective.status || '').toLowerCase() === 'done'
    ).length;
    const total = sprintObjectives.length;
    const todo = Math.max(0, total - done);
    const percent = total > 0 ? Math.round((done / total) * 100) : 0;
    return { total, done, todo, percent };
  }, [projectObjectives, activeSprint]);

  const sprintObjectivesByStatus = useMemo(() => {
    const buckets = {
      not_started: [],
      in_progress: [],
      blocked: [],
    };
    if (!activeSprint?.id) return buckets;
    (projectObjectives || []).forEach((objective) => {
      if (String(objective.sprint_id) !== String(activeSprint.id)) return;
      const status = String(objective.status || 'not_started').toLowerCase();
      if (status === 'done') return;
      if (buckets[status]) {
        buckets[status].push(objective);
      } else {
        buckets.not_started.push(objective);
      }
    });
    return buckets;
  }, [projectObjectives, activeSprint]);

  const sprintObjectivesCount = useMemo(() => (
    sprintObjectivesByStatus.not_started.length
    + sprintObjectivesByStatus.in_progress.length
    + sprintObjectivesByStatus.blocked.length
  ), [sprintObjectivesByStatus]);

  const sprintTokenTotal = Number(activeSprint?.total_tokens || 0);
  const sprintTokenDone = Number(activeSprint?.tokens_completed || 0);
  const sprintTokenPct = sprintTokenTotal > 0
    ? Math.min(100, Math.round((sprintTokenDone / sprintTokenTotal) * 100))
    : 0;
  const selectedProjectPriorityInfo = selectedProject
    ? getProjectPriorityInfo(projectPriorityDraft, DEFAULT_PROJECT_PRIORITY_VALUE)
    : null;

  const liveProjects = useMemo(
    () => meetingProjects.map((project) => ({ id: project.id, name: project.name || `Projet #${project.id}` })),
    [meetingProjects]
  );

  const handleProjectPriorityChange = async (event) => {
    const nextPriority = event.target.value;
    setProjectPriorityDraft(nextPriority);
    if (!selectedProject?.id || !userIsSecretary || !onUpdateProjectReview) return;

    const requestId = projectPriorityRequestRef.current + 1;
    projectPriorityRequestRef.current = requestId;
    setProjectPrioritySaveState('saving');
    const updated = await onUpdateProjectReview(selectedProject.id, {
      previous_status: currentProjectReview?.previous_status || null,
      next_milestone: currentProjectReview?.next_milestone || '',
      weekly_objective: currentProjectReview?.weekly_objective || '',
      priority: Number(nextPriority),
      notes: currentProjectReview?.notes || '',
    });
    if (projectPriorityRequestRef.current !== requestId) return;
    setProjectPrioritySaveState(updated ? 'saved' : 'error');
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md h-auto min-h-full overflow-visible flex flex-col">
      <div className="flex items-start justify-between mb-6 border-b border-slate-100 pb-4">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold text-indigo-600 uppercase tracking-wider mb-1">
            <RefreshCcw size={12} /> Réunion tactique
          </div>
          <h2 className="text-2xl font-bold text-slate-900">
            {selectedCircle?.name || 'Réunion'}
          </h2>
          <p className="text-xs text-slate-400 mt-1">
            Secrétaire : {secretary?.name || 'Non défini'}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Phase : {meetingPhaseInfo?.label || "Tour d'inclusion"}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex flex-col gap-2 mr-2">
            <div className="flex flex-wrap gap-2">
              {meetingParticipants.map((p) => {
                const isConnected = isParticipantConnected(p.id);
                const employee = employeeById[String(p.id)];
                const avatarUrl = employee?.picture || employee?.photo_url || employee?.avatar_url;
                const firstName = p.name ? p.name.split(' ')[0] : 'Membre';
                const isSecretary = String(p.id) === String(activeMeeting?.created_by);
                const canTransfer = userIsSecretary && !isSecretary && isConnected && Boolean(onTransferSecretary);

                return (
                  <div
                    key={p.id ?? p.name}
                    title={canTransfer ? `Transférer le rôle de secrétaire à ${p.name || 'ce membre'}` : `${p.name || 'Membre'}${isConnected ? ' (connecté)' : ' (absent)'}${isSecretary ? ' · secrétaire' : ''}`}
                    onClick={() => {
                      if (canTransfer) onTransferSecretary(p.id);
                    }}
                    className={`flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${isConnected
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                        : 'bg-slate-50 border-slate-200 text-slate-500 opacity-60'
                      } ${canTransfer ? 'cursor-pointer hover:bg-indigo-50 hover:border-indigo-200' : ''}`}
                  >
                    <div className={`relative w-5 h-5 rounded-full overflow-hidden ${isConnected ? 'ring-1 ring-emerald-500' : 'ring-1 ring-slate-300'
                      } bg-slate-200 flex-shrink-0`}>
                      {avatarUrl ? (
                        <img src={avatarUrl} alt={p.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[9px] font-bold uppercase text-slate-500">
                          {(p.name || '?').charAt(0)}
                        </div>
                      )}
                    </div>
                    <span>{firstName}</span>
                  </div>
                );
              })}
            </div>
            {userIsSecretary && onInviteParticipant && (
              <div className="flex flex-wrap gap-2">
                <select
                  value={inviteEmployeeId}
                  onChange={(e) => setInviteEmployeeId(e.target.value)}
                  className="min-w-[180px] border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:ring-2 focus:ring-indigo-100 outline-none"
                  disabled={availableInvitees.length === 0}
                >
                  <option value="">
                    {availableInvitees.length === 0 ? 'Aucune personne à inviter' : 'Inviter une personne'}
                  </option>
                  {availableInvitees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => {
                    if (!inviteEmployeeId) return;
                    onInviteParticipant(Number(inviteEmployeeId));
                    setInviteEmployeeId('');
                  }}
                  className="px-3 py-1.5 text-xs font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  disabled={!inviteEmployeeId}
                >
                  Inviter
                </button>
              </div>
            )}
          </div>

          <div className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border ${realtimeConnected ? 'bg-emerald-50 border-emerald-100 text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-500'}`}>
            <div className={`w-2 h-2 rounded-full ${realtimeConnected ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`} />
            {realtimeConnected ? 'En direct' : 'Hors ligne'}
          </div>

          {userIsSecretary && (
            <button
              type="button"
              onClick={() => onCloseMeeting && onCloseMeeting()}
              title="Clôturer la réunion (confirmation demandée)"
              className="px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 border border-red-100 rounded-lg hover:bg-red-100 transition-colors"
            >
              Clôturer
            </button>
          )}
        </div>
      </div>

      {meetingError && (
        <div className="mb-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 px-4 py-3 rounded-xl">
          <AlertCircle size={16} />
          {meetingError}
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-6 h-full overflow-visible">
        <div className="w-full lg:w-96 shrink-0 flex flex-col gap-4 h-full overflow-y-auto pr-2">
          {showProjectSidebar && (
            <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Projets</h3>
                <span className="text-[10px] text-slate-400">{meetingProjects.length}</span>
              </div>
              {meetingProjects.length === 0 ? (
                <p className="text-xs text-slate-400 italic">Aucun projet en cours.</p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {meetingProjects.map((project) => {
                    const isSelected = String(selectedMeetingProjectId) === String(project.id);
                    const projectPriorityInfo = getMeetingProjectPriorityInfo(project);
                    const reviewed = !isSelected && reviewedIds.has(String(project.id));
                    const recap = reviewed ? projectRecapState({
                      projectId: project.id, live: meetingLive, review: reviewByProjectId[String(project.id)]?.current,
                    }) : null;
                    return (
                      <button
                        key={project.id}
                        type="button"
                        onClick={() => {
                          if (!userIsSecretary || !onSelectMeetingProject) return;
                          onSelectMeetingProject(isSelected ? null : project.id);
                        }}
                        className={`w-full text-left p-2 rounded-lg border text-xs transition-colors ${isSelected
                          ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                          : 'bg-white border-slate-200 text-slate-600 hover:border-indigo-200'
                          } ${userIsSecretary ? 'cursor-pointer' : 'cursor-default'}`}
                        disabled={!userIsSecretary}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="flex min-w-0 items-center gap-1.5">
                            {recap?.pending ? (
                              <Loader2 size={12} className="shrink-0 animate-spin text-indigo-400" />
                            ) : reviewed ? (
                              <Check size={12} className="shrink-0 text-emerald-500" aria-label="Déjà passé" />
                            ) : null}
                            <span className="font-semibold truncate">{project.name}</span>
                          </span>
                          <div className="flex shrink-0 items-center gap-1">
                            {projectPriorityInfo && (
                              <span
                                className={`rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${projectPriorityInfo.className}`}
                                title={`Priorité du projet : P${projectPriorityInfo.value} - ${projectPriorityInfo.label}`}
                              >
                                P{projectPriorityInfo.value}
                              </span>
                            )}
                            {project.project_number !== undefined && project.project_number !== null && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-semibold">
                                #{project.project_number}
                              </span>
                            )}
                          </div>
                        </div>
                        {project.status && (
                          <div className="text-[10px] text-slate-400 mt-1">Statut: {projectStatusLabel(project.status)}</div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              {!userIsSecretary && meetingProjects.length > 0 && (
                <p className="text-[10px] text-slate-400">Seul le secrétaire choisit le projet.</p>
              )}
            </div>
          )}

          <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Ordre du jour</h3>
              <span className="text-[10px] text-slate-400">
                {openPointsCount} ouvert{openPointsCount > 1 ? 's' : ''} sur {visibleMeetingPoints.length}
              </span>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newPointTitle}
                onChange={(e) => setNewPointTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddPoint();
                  }
                }}
                maxLength={500}
                className="min-w-0 flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-100 outline-none"
                placeholder="Ajouter un point… (Entrée)"
              />
              <button
                type="button"
                onClick={handleAddPoint}
                className="inline-flex shrink-0 items-center gap-1 px-3 py-2 text-xs font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                disabled={!newPointTitle.trim()}
              >
                <Plus size={14} /> Ajouter
              </button>
            </div>
            {visibleMeetingPoints.length === 0 ? (
              <p className="text-xs text-slate-400 italic">
                Aucun point pour le moment : ajoute un point ou reprends un point préparé ci-dessous.
              </p>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {visibleMeetingPoints.map((point) => {
                  const isSelected = String(selectedMeetingPointId) === String(point.id);
                  const statusInfo = pointStatusInfo(point.status);
                  const isDone = String(point.status || 'open').toLowerCase() !== 'open';
                  return (
                    <button
                      type="button"
                      key={point.id}
                      onClick={() => userIsSecretary && onSelectMeetingPoint && onSelectMeetingPoint(point.id)}
                      disabled={!userIsSecretary}
                      className={`w-full text-left p-2 rounded-lg border text-sm transition-colors ${isSelected ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200 hover:border-indigo-200'} ${userIsSecretary ? 'cursor-pointer' : 'cursor-default'}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className={`font-medium ${isDone ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                          {point.title}
                        </span>
                        <span className={`shrink-0 text-[10px] px-2 py-0.5 rounded-full font-semibold ${statusInfo.className}`}>
                          {statusInfo.label}
                        </span>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        Par {point.author_name || 'un membre'}
                        {point.circle_point_id ? ' · préparé avant la réunion' : ''}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            <p className="text-[10px] text-slate-400">
              {userIsSecretary
                ? 'Clique sur un point pour le traiter, puis « Clore le point ».'
                : 'Le secrétaire choisit le point en cours.'}
            </p>
          </div>

          <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="flex items-center gap-1.5 text-xs font-bold text-slate-400 uppercase tracking-wider">
                <CalendarClock size={12} /> Points préparés
              </h3>
              <span className="text-[10px] text-slate-400">{preparedPendingCount} en attente</span>
            </div>
            <p className="text-[11px] text-slate-500">
              {userIsSecretary
                ? "Notés avant la réunion par les membres du cercle (onglet Points), absents compris. Ajoute ceux à traiter à l'ordre du jour."
                : "Tes points notés avant la réunion (onglet Points). Ajoute-les à l'ordre du jour pour en parler."}
            </p>
            {preparedPointsLoading && preparedPointGroups.length === 0 ? (
              <p className="flex items-center gap-2 text-xs text-slate-400">
                <Loader2 size={12} className="animate-spin" /> Chargement des points préparés…
              </p>
            ) : preparedPointGroups.length === 0 ? (
              <p className="text-xs text-slate-400 italic">Aucun point préparé.</p>
            ) : (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {preparedPointGroups.map((group) => (
                  <div key={group.authorId} className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <span className={`h-1.5 w-1.5 rounded-full ${group.connected ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                      <span className="text-xs font-semibold text-slate-600">{group.name}</span>
                      {!group.connected && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">absent</span>
                      )}
                    </div>
                    {group.items.map((item) => {
                      const canImport = userIsSecretary || group.isMe;
                      const importing = Boolean(importingPoints[String(item.id)]) && !item.onAgenda;
                      return (
                        <div
                          key={item.id}
                          className="flex items-start justify-between gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-2"
                        >
                          <span className={`min-w-0 text-sm break-words ${item.onAgenda ? 'text-slate-400' : 'text-slate-800'}`}>
                            {item.title}
                          </span>
                          {item.onAgenda ? (
                            <span className="inline-flex shrink-0 items-center gap-1 text-[10px] font-semibold text-indigo-600">
                              <Check size={11} /> Déjà à l'ordre du jour
                            </span>
                          ) : canImport ? (
                            <button
                              type="button"
                              onClick={() => handleImportPreparedPoint(item.id)}
                              disabled={importing || !realtimeConnected}
                              className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-indigo-100 bg-indigo-50 px-2 py-1 text-[11px] font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-50"
                            >
                              {importing ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />}
                              Ajouter à l'ordre du jour
                            </button>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Phases</h3>
              <span className="text-[10px] text-slate-400">{meetingPhaseInfo?.badge || 'Inclusion'}</span>
            </div>
            <div className="space-y-2">
              {meetingPhases.map((phase) => {
                const isActive = effectiveMeetingPhase === phase.key;
                return (
                  <button
                    key={phase.key}
                    type="button"
                    onClick={() => {
                      if (!userIsSecretary) return;
                      onSelectMeetingPhase && onSelectMeetingPhase(phase.key);
                    }}
                    className={`w-full text-left px-3 py-2 rounded-lg border text-xs transition-colors ${isActive
                      ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-indigo-200'
                      } ${userIsSecretary ? 'cursor-pointer' : 'cursor-default opacity-70'}`}
                    disabled={!userIsSecretary}
                  >
                    <div className="font-semibold">{phase.label}</div>
                    <div className="text-[10px] text-slate-400">{phase.helper}</div>
                  </button>
                );
              })}
            </div>
            {!userIsSecretary && (
              <p className="text-[10px] text-slate-400">Le secrétaire pilote la phase en cours.</p>
            )}
          </div>
        </div>

        <div className="flex-1 min-w-0 flex flex-col h-full overflow-y-auto bg-slate-50/50 rounded-xl border border-slate-200">
          <div className="flex-1 h-auto overflow-visible p-6">
            <div className="w-full max-w-6xl mx-auto space-y-6">
              <LiveSecretaryPanel
                apiUrl={apiUrl || apiBaseUrl}
                meetingId={activeMeeting?.id}
                getAuthHeaders={getAuthHeaders}
                live={meetingLive}
                userIsSecretary={userIsSecretary}
                projects={liveProjects}
              />
              {showInclusion && (
                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-4">
                  <div>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Tour d'inclusion</p>
                    <h3 className="text-xl font-bold text-slate-800 mt-1">Disponibilité des participants</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Chaque personne partage un mot pour dire qu'elle est disponible.
                    </p>
                  </div>
                  {meetingParticipants.length === 0 ? (
                    <p className="text-sm text-slate-400">Aucun participant connecté.</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {meetingParticipants.map((participant) => {
                        const isSecretary = String(participant.id) === String(activeMeeting?.created_by);
                        const isConnected = isParticipantConnected(participant.id);
                        return (
                          <div
                            key={participant.id || participant.name}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs ${isConnected
                              ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                              : 'bg-slate-50 border-slate-200 text-slate-500'
                              }`}
                          >
                            <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                            <span className="font-semibold">{participant.name}</span>
                            {isSecretary && (
                              <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-semibold">
                                Secrétaire
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {showProjects && (
                <>
                  <ReviewedProjects
                    order={reviewOrder}
                    currentProjectId={selectedMeetingProjectId}
                    projectsById={meetingProjectsById}
                    live={meetingLive}
                    reviewByProjectId={reviewByProjectId}
                    userIsSecretary={userIsSecretary}
                    onSave={liveActions?.saveProjectNews}
                    onSelect={onSelectMeetingProject}
                  />
                  <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                          Revue des projets
                          {selectedProjectIndex >= 0 && (
                            <span className="ml-2 normal-case font-semibold tracking-normal text-slate-400">
                              · {selectedProjectIndex + 1} sur {meetingProjects.length}
                            </span>
                          )}
                        </p>
                        <h3 className="text-xl font-bold text-slate-800 mt-1">
                          {selectedProject?.name || 'Sélectionnez un projet'}
                        </h3>
                        {selectedProject?.status && (
                          <p className="text-xs text-slate-400 mt-1">
                            Statut : {projectStatusLabel(selectedProject.status)}
                          </p>
                        )}
                        {userIsSecretary && onSelectMeetingProject && nextProject && (
                          <button
                            type="button"
                            onClick={() => onSelectMeetingProject(nextProject.id)}
                            className="mt-3 inline-flex max-w-full items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700"
                          >
                            <span className="truncate">{selectedProject ? 'Suivant' : 'Commencer'} : {nextProject.name}</span>
                            <ArrowRight size={13} className="shrink-0" />
                          </button>
                        )}
                      </div>
                      <div className="flex shrink-0 flex-wrap items-end justify-end gap-2">
                        {selectedProject && (
                          <label className="block">
                            <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                              Priorité du projet
                            </span>
                            <select
                              value={projectPriorityDraft}
                              onChange={handleProjectPriorityChange}
                              disabled={!userIsSecretary || projectPrioritySaveState === 'saving'}
                              className="mt-1 min-w-[150px] rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-100 disabled:bg-slate-50 disabled:text-slate-500"
                              title={selectedProjectPriorityInfo
                                ? `Priorité projet : P${selectedProjectPriorityInfo.value} - ${selectedProjectPriorityInfo.label}`
                                : 'Priorité du projet'}
                            >
                              {PROJECT_PRIORITY_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  P{option.value} - {option.label}
                                </option>
                              ))}
                            </select>
                            {userIsSecretary && projectPrioritySaveState !== 'idle' && (
                              <span className={`mt-1 block text-[10px] ${projectPrioritySaveState === 'error' ? 'text-rose-500' : projectPrioritySaveState === 'saved' ? 'text-emerald-600' : 'text-amber-600'}`}>
                                {projectPrioritySaveState === 'saving'
                                  ? 'Enregistrement...'
                                  : projectPrioritySaveState === 'saved'
                                    ? 'Priorité enregistrée'
                                    : "Erreur d'enregistrement"}
                              </span>
                            )}
                          </label>
                        )}
                        {selectedProject?.project_number !== undefined && selectedProject?.project_number !== null && (
                          <span className="text-[10px] uppercase px-2 py-1 rounded-full bg-slate-100 text-slate-600 font-semibold">
                            #{selectedProject.project_number}
                          </span>
                        )}
                      </div>
                    </div>

                    {!selectedMeetingProjectId ? (
                      <p className="text-sm text-slate-400">Choisissez un projet pour démarrer la revue.</p>
                    ) : (
                      <div className="space-y-4">
                        {/* Roadmap et budget d'abord (nouvelle organisation, 20/10) : itération en cours, temps et achats consommés
                            face au budgété, % de réalisation fixé par le titulaire de la roadmap, derniers changements tracés. */}
                        <ProjectSteering projectId={selectedMeetingProjectId} />
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
                            <p className="text-[10px] uppercase text-slate-400 font-semibold">Sprint</p>
                            <p className="text-sm font-semibold text-slate-800">
                              {activeSprint
                                ? activeSprint.description || activeSprint.name || `Sprint ${activeSprint.id}`
                                : 'Aucun sprint en cours'}
                            </p>
                            {activeSprint && sprintDatesLabel && (
                              <p className="text-[10px] text-slate-400 mt-1">{sprintDatesLabel}</p>
                            )}
                          </div>
                          <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
                            <p className="text-[10px] uppercase text-slate-400 font-semibold">Objectifs</p>
                            {activeSprint ? (
                              <>
                                <p className="text-lg font-bold text-slate-800">
                                  {sprintObjectiveSummary.done} / {sprintObjectiveSummary.total}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {sprintObjectiveSummary.todo} à faire
                                </p>
                              </>
                            ) : (
                              <p className="text-sm text-slate-400">Aucun objectif actif.</p>
                            )}
                          </div>
                          <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
                            <p className="text-[10px] uppercase text-slate-400 font-semibold">Tokens sprint</p>
                            {activeSprint ? (
                              <>
                                <p className="text-lg font-bold text-slate-800">
                                  {formatNumber(sprintTokenDone)} / {formatNumber(sprintTokenTotal)}
                                </p>
                                <p className="text-xs text-slate-500">{sprintTokenPct} % réalisé</p>
                              </>
                            ) : (
                              <p className="text-sm text-slate-400">Aucun sprint en cours.</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <ProjectNewsCard
                    live={meetingLive}
                    projectId={selectedMeetingProjectId}
                    review={currentProjectReview}
                    userIsSecretary={userIsSecretary}
                    onSave={liveActions?.saveProjectNews}
                  />

                  <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-3">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Participants (focus)</p>
                        <p className="text-sm text-slate-500 mt-1">Heures saisies sur {rangeLabel}</p>
                      </div>
                      {selectedMeetingProjectId && (
                        <span className="text-[10px] uppercase px-2 py-1 rounded-full bg-slate-100 text-slate-600 font-semibold">
                          {projectFocusParticipants.length} personnes
                        </span>
                      )}
                    </div>

                    {!selectedMeetingProjectId ? (
                      <p className="text-sm text-slate-400">Choisissez un projet pour afficher les participants.</p>
                    ) : projectFocusLoading ? (
                      <p className="text-sm text-slate-400">Chargement des focus...</p>
                    ) : projectFocusError ? (
                      <p className="text-sm text-red-500">{projectFocusError}</p>
                    ) : projectFocusParticipants.length === 0 ? (
                      <p className="text-sm text-slate-400">Aucun participant avec focus sur ce projet.</p>
                    ) : (
                      <div className="space-y-2">
                        {projectHoursLoading && (
                          <p className="text-xs text-slate-400">Chargement des heures...</p>
                        )}
                        {projectHoursError && (
                          <p className="text-xs text-red-500">{projectHoursError}</p>
                        )}
                        {projectFocusParticipants.map((participant) => {
                          const hoursValue = projectHoursByEmployee[String(participant.employeeId)] || 0;
                          const hoursLabel = projectHoursLoading ? '...' : `${formatNumber(hoursValue, 1)} h`;
                          const initials = participant.name
                            ? participant.name.split(' ').map((part) => part[0]).join('').toUpperCase()
                            : '?';
                          return (
                            <div
                              key={participant.employeeId}
                              className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                {participant.picture ? (
                                  <img
                                    src={participant.picture}
                                    alt={participant.name}
                                    className="h-8 w-8 rounded-full border border-slate-200 object-cover"
                                  />
                                ) : (
                                  <div className="h-8 w-8 rounded-full bg-slate-200 text-[10px] font-semibold text-slate-600 flex items-center justify-center">
                                    {initials}
                                  </div>
                                )}
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-slate-800 truncate">{participant.name}</p>
                                  {participant.roles.length > 0 && (
                                    <p className="text-[10px] text-slate-400 uppercase tracking-wide truncate">
                                      {participant.roles.join(' / ')}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-semibold text-slate-800">{hoursLabel}</p>
                                <p className="text-[10px] text-slate-400">{rangeTitle}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-4">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Tâches du sprint</p>
                      {activeSprint?.id && (
                        <span className="text-[10px] text-slate-400">
                          {sprintObjectivesCount} tâches
                        </span>
                      )}
                    </div>
                    {!selectedMeetingProjectId ? (
                      <p className="text-sm text-slate-400">Choisissez un projet pour afficher les tâches.</p>
                    ) : !activeSprint ? (
                      <p className="text-sm text-slate-400">Aucun sprint en cours pour ce projet.</p>
                    ) : objectivesLoading ? (
                      <p className="text-sm text-slate-400">Chargement des tâches...</p>
                    ) : objectivesError ? (
                      <p className="text-sm text-red-500">{objectivesError}</p>
                    ) : sprintObjectivesCount === 0 ? (
                      <p className="text-sm text-slate-400">Aucune tâche en cours pour ce sprint.</p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {[
                          { key: 'not_started', title: 'À faire', badge: 'bg-slate-100 text-slate-600', items: sprintObjectivesByStatus.not_started },
                          { key: 'in_progress', title: 'En cours', badge: 'bg-indigo-100 text-indigo-700', items: sprintObjectivesByStatus.in_progress },
                          { key: 'blocked', title: 'Bloqué', badge: 'bg-rose-100 text-rose-700', items: sprintObjectivesByStatus.blocked },
                        ].map((col) => (
                          <div key={col.key} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <div className="flex items-center justify-between mb-2">
                              <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full ${col.badge}`}>
                                {col.title}
                              </span>
                              <span className="text-[10px] text-slate-400">{col.items.length}</span>
                            </div>
                            {col.items.length === 0 ? (
                              <p className="text-xs text-slate-400 italic">Aucune tâche</p>
                            ) : (
                              <div className="space-y-1">
                                {col.items.slice(0, 6).map((objective) => (
                                  <div key={objective.id} className="text-xs text-slate-600 truncate">
                                    {objective.name}
                                  </div>
                                ))}
                                {col.items.length > 6 && (
                                  <div className="text-[10px] text-slate-400">+{col.items.length - 6} autres</div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}

              {showFocus && (
                <FocusTechLive
                  meetingFocus={meetingFocus}
                  meetingFocusList={meetingFocusList}
                  focusActions={focusActions}
                  userIsSecretary={userIsSecretary}
                  currentUserId={currentUserId}
                  connectedUsers={connectedUsers}
                  onSelectMeetingPhase={onSelectMeetingPhase}
                />
              )}

              {showAgenda && (
                <>
                  <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-3">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Ordre du jour</p>
                        <h3 className="text-xl font-bold text-slate-800 mt-1">Points à traiter</h3>
                        <p className="text-sm text-slate-500 mt-1">
                          Ajoutez des points ou reprenez les points préparés (colonne de gauche), puis traitez-les un par un.
                        </p>
                      </div>
                      <span className="text-[10px] uppercase px-2 py-1 rounded-full bg-slate-100 text-slate-600 font-semibold">
                        {openPointsCount} ouvert{openPointsCount > 1 ? 's' : ''} sur {visibleMeetingPoints.length}
                      </span>
                    </div>
                  </div>

                  {selectedPoint ? (
                    <>
                      <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-2">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="text-xl font-bold text-slate-800">{selectedPoint.title}</h3>
                            <p className="text-xs text-slate-500 mt-1">Proposé par {selectedPoint.author_name || 'un membre'}</p>
                          </div>
                          <span className={`shrink-0 text-[10px] uppercase px-2 py-1 rounded-full font-semibold ${pointStatusInfo(selectedPoint.status).className}`}>
                            {pointStatusInfo(selectedPoint.status).label}
                          </span>
                        </div>
                      </div>

                      <PointSummaryCard
                        live={meetingLive}
                        point={selectedPoint}
                        userIsSecretary={userIsSecretary}
                        onSave={liveActions?.savePointSummary}
                      />

                      <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-3">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-bold text-slate-600 uppercase tracking-wider">Notes du point</h4>
                          <span className="text-[10px] text-slate-400">Partagées</span>
                        </div>
                        <textarea
                          value={pointNotesDraft}
                          onChange={(e) => {
                            const next = e.target.value;
                            setPointNotesDraft(next);
                            pointNotesTypingRef.current = true;
                            if (pointNotesTypingTimer.current) clearTimeout(pointNotesTypingTimer.current);
                            pointNotesTypingTimer.current = setTimeout(() => {
                              pointNotesTypingRef.current = false;
                            }, 500);
                            onPointNotesChange && onPointNotesChange(selectedPoint.id, next);
                          }}
                          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm min-h-[140px] focus:ring-2 focus:ring-indigo-100 outline-none"
                          placeholder="Notes pour ce point..."
                        />
                      </div>

                      <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-4">
                        <h4 className="text-sm font-bold text-slate-600 uppercase tracking-wider">Actions prises</h4>
                        {selectedPointActions.length === 0 ? (
                          <p className="text-xs text-slate-400 italic">Aucune action enregistrée.</p>
                        ) : (
                          <div className="space-y-2">
                            {selectedPointActions.map((action, idx) => {
                              const type = (action?.type || '').toLowerCase();
                              return (
                                <div key={`${type}-${idx}`} className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                                  <div className="text-sm font-semibold text-slate-800">
                                    {type === 'objective' ? 'Objectif' : type === 'task' ? 'Tâche' : 'Action'} : {action?.name || 'Sans titre'}
                                  </div>
                                  <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-2">
                                    {action?.project_id && (
                                      <span className="px-2 py-0.5 rounded-full bg-white border border-slate-200">
                                        Projet : {getProjectName(action.project_id)}
                                      </span>
                                    )}
                                    {action?.owner_id && (
                                      <span className="px-2 py-0.5 rounded-full bg-white border border-slate-200">
                                        Responsable : {getEmployeeName(action.owner_id)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {userIsSecretary && (
                          <div className="flex flex-wrap gap-2 pt-2">
                            <button
                              onClick={openTaskComposer}
                              className="px-3 py-2 text-xs font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                            >
                              Créer une tâche
                            </button>
                            <button
                              onClick={openObjectiveComposer}
                              className="px-3 py-2 text-xs font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                            >
                              Créer un objectif
                            </button>
                          </div>
                        )}

                        {userIsSecretary && (selectedPoint.status || 'open').toLowerCase() === 'open' && (
                          <div className="flex justify-end pt-2">
                            <button
                              onClick={() => onPointDecision && onPointDecision(selectedPoint.id, 'treated')}
                              className="px-4 py-2 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg hover:bg-emerald-100"
                            >
                              Clore le point
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-slate-400">
                      <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                        <ArrowRight size={24} className="text-slate-300" />
                      </div>
                      <p>Sélectionnez un point pour voir les détails</p>
                    </div>
                  )}
                </>
              )}

              {showClosure && (
                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-3">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Clôture</p>
                  <h3 className="text-xl font-bold text-slate-800">Fin de réunion</h3>
                  <p className="text-sm text-slate-500">
                    Vérifiez que les points sont traités et que les actions sont notées.
                    {openPointsCount > 0 && ` Encore ${openPointsCount} point${openPointsCount > 1 ? 's' : ''} ouvert${openPointsCount > 1 ? 's' : ''} : les points préparés non traités restent en attente pour la prochaine réunion.`}
                  </p>
                  {userIsSecretary ? (
                    <button
                      type="button"
                      onClick={() => onCloseMeeting && onCloseMeeting()}
                      className="px-4 py-2 text-xs font-semibold text-red-600 bg-red-50 border border-red-100 rounded-lg hover:bg-red-100"
                    >
                      Clôturer la réunion
                    </button>
                  ) : (
                    <p className="text-xs text-slate-400">Le secrétaire clôture la réunion.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showTaskModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden relative">
            {userIsSecretary && (
              <button
                className="absolute top-3 right-3 rounded-full p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                onClick={closePointComposer}
                aria-label="Fermer"
              >
                <X size={16} />
              </button>
            )}
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-700">Nouvelle tâche</p>
                {!userIsSecretary && (
                  <span className="text-[10px] uppercase px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 font-semibold">
                    Saisie en direct
                  </span>
                )}
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold text-slate-500">Nom</label>
                  <input
                    type="text"
                    value={taskForm.name}
                    onChange={(e) => updateTaskComposer({ name: e.target.value })}
                    className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                    placeholder="Nom de la tâche"
                    disabled={!userIsSecretary}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500">Responsable</label>
                  <select
                    value={taskForm.owner_id}
                    onChange={(e) => updateTaskComposer({ owner_id: e.target.value })}
                    className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                    disabled={!userIsSecretary}
                  >
                    <option value="">Choisir une personne</option>
                    {activeEmployees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500">Projet</label>
                  <select
                    value={taskForm.project_id}
                    onChange={(e) => updateTaskComposer({ project_id: e.target.value })}
                    className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                    disabled={!userIsSecretary}
                  >
                    <option value="">Sans projet</option>
                    {taskProjectOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                {userIsSecretary ? (
                  <>
                    <button
                      onClick={closePointComposer}
                      className="px-4 py-2 text-sm text-slate-600 rounded-lg border border-slate-200"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={async () => {
                        if (!taskForm.name.trim() || !taskForm.owner_id) return;
                        const created = await (onCreatePointTask && onCreatePointTask(selectedPoint.id, {
                          name: taskForm.name.trim(),
                          owner_id: Number(taskForm.owner_id),
                          project_id: taskForm.project_id ? Number(taskForm.project_id) : null,
                        }));
                        if (created) closePointComposer();
                      }}
                      className="px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                      disabled={!taskForm.name.trim() || !taskForm.owner_id}
                    >
                      Créer
                    </button>
                  </>
                ) : (
                  <p className="text-xs text-slate-400">Le secrétaire saisit cette action.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showObjectiveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden relative">
            {userIsSecretary && (
              <button
                className="absolute top-3 right-3 rounded-full p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                onClick={closePointComposer}
                aria-label="Fermer"
              >
                <X size={16} />
              </button>
            )}
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-700">Nouvel objectif</p>
                {!userIsSecretary && (
                  <span className="text-[10px] uppercase px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold">
                    Saisie en direct
                  </span>
                )}
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold text-slate-500">Nom</label>
                  <input
                    type="text"
                    value={objectiveForm.name}
                    onChange={(e) => updateObjectiveComposer({ name: e.target.value })}
                    className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                    placeholder="Nom de l'objectif"
                    disabled={!userIsSecretary}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500">Projet</label>
                  <input
                    type="text"
                    list={objectiveProjectDatalistId}
                    value={objectiveProjectSearch}
                    onChange={(e) => {
                      const nextValue = e.target.value || '';
                      setObjectiveProjectSearch(nextValue);
                      const normalized = nextValue.trim().toLowerCase();
                      const exactMatch = objectiveProjectOptions.find(
                        (option) => option.label.toLowerCase() === normalized
                      );
                      if (exactMatch) {
                        if (String(objectiveForm.project_id || '') !== String(exactMatch.id)) {
                          updateObjectiveComposer({ project_id: String(exactMatch.id) });
                        }
                        return;
                      }
                      if (!normalized) {
                        if (objectiveForm.project_id) updateObjectiveComposer({ project_id: '' });
                        return;
                      }
                      if (objectiveForm.project_id && selectedObjectiveProjectLabel && nextValue !== selectedObjectiveProjectLabel) {
                        updateObjectiveComposer({ project_id: '' });
                      }
                    }}
                    onBlur={() => {
                      if (!objectiveForm.project_id || !selectedObjectiveProjectLabel) return;
                      if (objectiveProjectSearch !== selectedObjectiveProjectLabel) {
                        setObjectiveProjectSearch(selectedObjectiveProjectLabel);
                      }
                    }}
                    className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                    placeholder="Rechercher et sélectionner un projet…"
                    disabled={!userIsSecretary}
                  />
                  <datalist id={objectiveProjectDatalistId}>
                    {objectiveProjectOptions.map((option) => (
                      <option key={option.id} value={option.label} />
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500">Responsable (facultatif)</label>
                  <select
                    value={objectiveForm.owner_id}
                    onChange={(e) => updateObjectiveComposer({ owner_id: e.target.value })}
                    className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                    disabled={!userIsSecretary}
                  >
                    <option value="">Choisir une personne</option>
                    {activeEmployees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500">Tokens (effort)</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={objectiveForm.effort}
                    onChange={(e) => updateObjectiveComposer({ effort: e.target.value })}
                    className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                    placeholder="Ex: 5"
                    disabled={!userIsSecretary}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500">Description (facultatif)</label>
                  <textarea
                    value={objectiveForm.description}
                    onChange={(e) => updateObjectiveComposer({ description: e.target.value })}
                    className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm min-h-[90px]"
                    disabled={!userIsSecretary}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                {userIsSecretary ? (
                  <>
                    <button
                      onClick={closePointComposer}
                      className="px-4 py-2 text-sm text-slate-600 rounded-lg border border-slate-200"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={async () => {
                        if (!objectiveForm.name.trim() || !objectiveForm.project_id) return;
                        const effortValue = Number.parseInt(objectiveForm.effort, 10);
                        const created = await (onCreatePointObjective && onCreatePointObjective(selectedPoint.id, {
                          name: objectiveForm.name.trim(),
                          project_id: Number(objectiveForm.project_id),
                          owner_id: objectiveForm.owner_id ? Number(objectiveForm.owner_id) : null,
                          effort: Number.isInteger(effortValue) && effortValue >= 0 ? effortValue : 1,
                          description: objectiveForm.description || '',
                        }));
                        if (created) closePointComposer();
                      }}
                      className="px-4 py-2 text-sm font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                      disabled={!objectiveForm.name.trim() || !objectiveForm.project_id}
                    >
                      Créer
                    </button>
                  </>
                ) : (
                  <p className="text-xs text-slate-400">Le secrétaire saisit cette action.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeetingPanelTactical;
